import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  SelectComponent,
  Switch,
} from '@data-delivery/d3s';
import type { Control } from 'react-hook-form';
import type { UserFormValues } from '../schemas/user-form.schema';

interface SelectOption {
  value: string;
  label: string;
}

interface UserRolesSectionProps {
  control: Control<UserFormValues>;
  showBusinessGroup: boolean;
  showAssignments: boolean;
  canEditLoginAsAdmin: boolean;
  selectedBusinessGroupId?: string;
  businessGroupOptions: SelectOption[];
  contentAccessRoleOptions: SelectOption[];
  assignmentOptions: SelectOption[];
}

export function UserRolesSection({
  control,
  showBusinessGroup,
  showAssignments,
  canEditLoginAsAdmin,
  selectedBusinessGroupId,
  businessGroupOptions,
  contentAccessRoleOptions,
  assignmentOptions,
}: UserRolesSectionProps) {
  return (
    <div className='rounded-md border p-4 text-sm'>
      <p className='font-medium mb-2'>Roles & Ownership</p>
      <div className='grid grid-cols-12 gap-4'>
        {showBusinessGroup && (
          <div className='col-span-12 md:col-span-4'>
            <FormItem className='flex flex-col gap-1'>
              <FormLabel>Business Group *</FormLabel>
              <FormControl>
                <SelectComponent
                  placeholder='Select business group'
                  value={
                    selectedBusinessGroupId
                      ? [
                          {
                            value: selectedBusinessGroupId,
                            label:
                              businessGroupOptions.find(o => o.value === selectedBusinessGroupId)
                                ?.label || selectedBusinessGroupId,
                          },
                        ]
                      : []
                  }
                  options={businessGroupOptions}
                  isSearchable={false}
                  isMulti={false}
                  isDisabled
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          </div>
        )}

        <div className='col-span-12 md:col-span-4'>
          <FormField
            control={control}
            name='contentAccessRole'
            render={({ field }) => (
              <FormItem className='flex flex-col gap-1'>
                <FormLabel>Content Access Role *</FormLabel>
                <FormControl>
                  <SelectComponent
                    placeholder='Select content access role'
                    value={
                      field.value
                        ? [
                            {
                              value: field.value,
                              label:
                                contentAccessRoleOptions.find(o => o.value === field.value)
                                  ?.label || field.value,
                            },
                          ]
                        : []
                    }
                    options={contentAccessRoleOptions}
                    isSearchable
                    isMulti={false}
                    onChange={val => {
                      const selected = Array.isArray(val)
                        ? val[0]
                        : (val as { value: string } | undefined);
                      field.onChange(selected?.value || '');
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className='col-span-12 md:col-span-4'>
          <FormField
            control={control}
            name='orgName'
            render={({ field }) => (
              <FormItem className='flex flex-col gap-1'>
                <FormLabel>Org Name</FormLabel>
                <FormControl>
                  <Input {...field} placeholder='Acme Corp' />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        {canEditLoginAsAdmin && (
          <div className='col-span-12 md:col-span-4'>
            <FormField
              control={control}
              name='isLoginAdmin'
              render={({ field }) => (
                <FormItem className='flex flex-col gap-1'>
                  <FormLabel>Login As Admin</FormLabel>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}

        {showAssignments && (
          <div className='col-span-12'>
            <FormField
              control={control}
              name='assignments'
              render={({ field }) => (
                <FormItem className='flex flex-col gap-1'>
                  <FormLabel>Assignments *</FormLabel>
                  <FormControl>
                    <SelectComponent
                      placeholder='Select assignments'
                      value={(field.value || []).map((id: string) => ({
                        value: id,
                        label: assignmentOptions.find(o => o.value === id)?.label || id,
                      }))}
                      options={assignmentOptions}
                      isSearchable
                      isMulti
                      onChange={val => {
                        const arr = Array.isArray(val) ? val : val ? [val] : [];
                        field.onChange(arr.map(v => (v as { value: string }).value));
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}
      </div>
    </div>
  );
}
